import React from 'react';
import PersonIcon from '@mui/icons-material/Person';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import useFitnessVillageCopy from '../../../../../hooks/useFitnessVillageCopy';
import { DfcVillageHeaderState } from '../../hooks/useDfcVillageHeader';
import VillageLogo, { useHasDarkHeaderLogo } from './VillageLogo';
import ThemeSwitch from './ThemeSwitch';
import LanguageSwitch from './LanguageSwitch';
import {
  ActionGroup,
  CartBadge,
  HeaderBar,
  HeaderRow,
  LogoPlate,
  MenuBar,
  MenuButton,
  PillButton,
  RoundButton,
} from './style';

interface MobileHeaderProps {
  state: DfcVillageHeaderState;
  toggleDrawer: (open: boolean) => void;
}

/**
 * Under 960px: lockup on the left; the theme orb, cart (when filled), sign in / account and the
 * menu on the right.
 */
const MobileHeader: React.FC<MobileHeaderProps> = ({ state, toggleDrawer }) => {
  const {
    surface,
    handleLogoClick,
    showCartButton,
    cartItemsCount,
    handleNavigateToCart,
    showAccount,
    isSignedIn,
    enableQRCode,
    handleAccountClick,
    handleSignIn,
    showMenuButton,
  } = state;
  const copy = useFitnessVillageCopy().header;
  const hasDarkLogo = useHasDarkHeaderLogo();

  return (
    <HeaderBar surface={surface} position="sticky" elevation={0}>
      <HeaderRow>
        <LogoPlate surface={surface} bare={hasDarkLogo} onClick={handleLogoClick} aria-label={copy.home}>
          <VillageLogo />
        </LogoPlate>

        <ActionGroup>
          <LanguageSwitch surface={surface} variant="short" />
          <ThemeSwitch surface={surface} variant="orb" />

          {showCartButton && (
            <RoundButton
              surface={surface}
              onClick={handleNavigateToCart}
              aria-label={copy.cart}
              data-testid="cart-button"
            >
              <CartBadge badgeContent={cartItemsCount}>
                <ShoppingCartOutlinedIcon />
              </CartBadge>
            </RoundButton>
          )}

          {showAccount &&
            (isSignedIn ? (
              <RoundButton
                surface={surface}
                onClick={handleAccountClick}
                aria-label={enableQRCode ? copy.quickCheckIn : copy.account}
                data-testid="account-button"
              >
                {enableQRCode ? <QrCode2Icon /> : <PersonIcon />}
              </RoundButton>
            ) : (
              <PillButton surface={surface} variant="outline" size="sm" onClick={handleSignIn}>
                {copy.signIn}
              </PillButton>
            ))}

          {showMenuButton && (
            <MenuButton
              surface={surface}
              onClick={() => toggleDrawer(true)}
              aria-label={copy.openMenu}
              data-testid="top-nav-menu-button"
            >
              <MenuBar surface={surface} />
              <MenuBar surface={surface} />
              <MenuBar surface={surface} />
            </MenuButton>
          )}
        </ActionGroup>
      </HeaderRow>
    </HeaderBar>
  );
};

export default MobileHeader;
